'use client'

import * as React from 'react'
import { cn } from '../../helpers/utils.js'

import Badge from '../../components/badge.js'
import { Text } from '../../components/text.js'
import Button from '../../components/button.js'
import Picture from '../../components/picture.js'
import { Heading } from '../../components/heading.js'
import type { ProductCardProps, ButtonFunctionType, ImageObjectPosition } from './types.js'

/**
 * 图片裁切位置映射
 */
const objectPositionClassMap: Record<ImageObjectPosition, string> = {
  center: 'object-center',
  top: 'object-top',
  bottom: 'object-bottom',
  left: 'object-left',
  right: 'object-right',
  'top-left': 'object-left-top',
  'top-right': 'object-right-top',
  'bottom-left': 'object-left-bottom',
  'bottom-right': 'object-right-bottom',
}

/**
 * ProductCard - ImageOverlayShelf 的单个产品卡片
 * 图文叠加样式，背景图完全覆盖卡片
 */
const ProductCard = React.forwardRef<HTMLDivElement, ProductCardProps>(
  (
    {
      product,
      className,
      showTags = true,
      showOriginalPrice = true,
      onLearnMore,
      onShopNow,
      onAddToCart,
      onProductImageClick,
      classNames,
      secondaryButtonText,
      secondaryButtonFun,
      primaryButtonText,
      primaryButtonFun,
      copy,
    },
    ref
  ) => {
    const [primaryLoading, setPrimaryLoading] = React.useState(false)
    const [secondaryLoading, setSecondaryLoading] = React.useState(false)

    const isSoldOut = !product.availableForSale

    // 根据按钮功能类型调用相应的回调函数
    const handleButtonClick = async (buttonFun?: ButtonFunctionType, buttonType?: 'primary' | 'secondary') => {
      if (!buttonFun) return

      const setLoading = buttonType === 'primary' ? setPrimaryLoading : setSecondaryLoading
      setLoading(true)

      try {
        switch (buttonFun) {
          case 'buyNow':
            await onShopNow?.(product)
            break
          case 'addCart':
            await onAddToCart?.(product)
            break
          case 'learnMore':
            await onLearnMore?.(product)
            break
          default:
            break
        }
      } finally {
        setLoading(false)
      }
    }

    const displayTitle = product.custom_name || product.name
    const displayDescription = product.custom_description || product.description
    const theme = product.custom_theme ?? 'dark'

    return (
      <div
        ref={ref}
        className={cn(
          'rounded-box relative box-border w-full cursor-pointer overflow-hidden duration-300',
          'desktop:h-[384px] lg-desktop:h-[480px] h-[360px]',
          'tablet:min-w-[auto] min-w-[296px] max-w-[824px]',
          {
            'aiui-dark': theme === 'dark',
          },
          className,
          classNames?.productCard
        )}
      >
        <div className="box-border flex h-full flex-col justify-between overflow-hidden">
          {/* 背景图 */}
          <a
            onClick={() => onProductImageClick?.(product)}
            {...(!onProductImageClick &&
              product.listingLink && {
                href: product.listingLink,
              })}
            rel="noreferrer"
            className={cn('absolute inset-0', classNames?.productImage)}
          >
            <Picture
              alt={product.name}
              source={product.custom_image ?? product.image}
              className={cn(
                'rounded-box h-full overflow-hidden object-cover transition-all duration-300 [&_img]:size-full'
              )}
              imgClassName={cn('object-cover', objectPositionClassMap[product.imageObjectPosition ?? 'center'])}
            />
          </a>
          {/* 内容区域 */}
          <div
            className={cn(
              'desktop:p-6 collection-shelves-product-content pointer-events-none z-10 box-border flex flex-1 flex-col justify-end overflow-hidden p-4',
              classNames?.productContent
            )}
          >
            {/* 标签 */}
            {showTags && product.tags?.length ? (
              <div className={cn('mb-2 box-border flex flex-wrap gap-1 overflow-visible', classNames?.tagsContainer)}>
                {product.tags.map((tag: any, index: number) =>
                  (tag as any).label ? (
                    <Badge
                      key={index}
                      size="sm"
                      variant={(tag.variant as any) || 'outline'}
                      promotionalType={tag.promotionalType}
                    >
                      {tag.label}
                    </Badge>
                  ) : (
                    React.isValidElement(tag) && <React.Fragment key={index}>{tag}</React.Fragment>
                  )
                )}
              </div>
            ) : null}

            {/* 产品标题 */}
            {displayTitle ? (
              <Heading
                as="h3"
                title={displayTitle}
                html={displayTitle}
                className={cn(
                  'lg-desktop:text-2xl lg-desktop:leading-7 tablet:text-xl text-info-primary tablet:leading-6 line-clamp-2 text-base font-bold',
                  classNames?.productTitle
                )}
              />
            ) : null}

            {/* 产品描述 */}
            {displayDescription ? (
              <Text
                size={2}
                html={displayDescription}
                className={cn(
                  'lg-desktop:text-lg desktop:text-base text-info-primary line-clamp-1 text-sm font-bold',
                  classNames?.productDescription
                )}
              />
            ) : null}

            {/* 价格区域 */}
            <div className={cn('mb-1 mt-4 flex items-center', classNames?.priceContainer)}>
              {isSoldOut ? (
                <div className={cn('tablet:text-2xl text-info-primary text-xl font-bold', classNames?.productPrice)}>
                  {copy?.outOfStockLabel ?? 'Sold Out'}
                </div>
              ) : (
                <>
                  <div className={cn('tablet:text-2xl text-info-primary text-xl font-bold', classNames?.productPrice)}>
                    {product.price}
                  </div>
                  {showOriginalPrice && product.originalPrice && (
                    <div
                      className={cn(
                        'tablet:text-xl text-info-secondary ml-1 text-lg font-bold line-through',
                        classNames?.originalPrice
                      )}
                    >
                      {product.originalPrice}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* 按钮区域 */}
            <div
              className={cn('lg-desktop:gap-3 pointer-events-auto flex items-center gap-2', classNames?.buttonGroup)}
            >
              {secondaryButtonText && (
                <Button
                  variant="secondary"
                  onClick={() => handleButtonClick(secondaryButtonFun, 'secondary')}
                  disabled={isSoldOut && secondaryButtonFun !== 'learnMore'}
                  loading={secondaryLoading}
                  className={cn(classNames?.secondaryButton)}
                  data-headless-type-name="ImageOverlayShelf#ProductCard"
                  data-headless-title-desc-button={`${displayTitle}#${displayDescription || ''}#${secondaryButtonText}`}
                  data-headless-sku={product.sku}
                >
                  {secondaryButtonText}
                </Button>
              )}
              {primaryButtonText && (
                <Button
                  variant="primary"
                  onClick={() => handleButtonClick(primaryButtonFun, 'primary')}
                  disabled={isSoldOut && primaryButtonFun !== 'learnMore'}
                  loading={primaryLoading}
                  className={cn(classNames?.primaryButton)}
                  data-headless-type-name="ImageOverlayShelf#ProductCard"
                  data-headless-title-desc-button={`${displayTitle}#${displayDescription || ''}#${primaryButtonText}`}
                  data-headless-sku={product.sku}
                >
                  {primaryButtonText}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    )
  }
)

ProductCard.displayName = 'ImageOverlayShelf.ProductCard'

export { ProductCard }
export default ProductCard
